import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ExcelService } from 'src/app/services/excel.service';
import { LotteryService, LotteryDrillDownRow, LotterySubsidyPattern } from 'src/app/services/lottery.service';
import { LotteryDrillDownContext } from '../lottery-dashboard/lottery-dashboard.component';
import { SubsidyPatternViewComponent } from '../subsidy-pattern-view/subsidy-pattern-view.component';

@Component({
  selector: 'app-lottery-subdivision',
  templateUrl: './lottery-subdivision.component.html',
  styleUrls: ['./lottery-subdivision.component.scss']
})
export class LotterySubdivisionComponent implements OnInit {

  @Input() drillData: LotteryDrillDownContext;
  @Input() divisionId: number;
  @Input() divisionName: string;
  @Output() blockClosed = new EventEmitter<boolean>();
  @Output() toDashboardComp = new EventEmitter<boolean>();

  subDivisions: LotteryDrillDownRow[] = [];
  filteredSubDivisions: LotteryDrillDownRow[] = [];
  loading = true;

  villageView = false;
  selectedSubDivision: { id: number; name: string };

  showOnlyWithTarget = true;
  private searchText = '';

  totalTarget = 0;
  totalApplied = 0;
  totalAssigned = 0;
  totalBalanceTarget = 0;
  totalVillageCount = 0;

  private readonly excelColumns: string[] = [
    'Name', 'Lottery Executed Villages', 'Total Target', 'Applications Received', 'Benefitted Count', 'Balance Target',
    'Application Percentage', 'Benefitted Percentage'
  ];

  constructor(
    private excelService: ExcelService,
    private lotteryService: LotteryService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.lotteryService
      .getDrillDownList(
        this.drillData.financialYearId, this.drillData.departmentId, this.drillData.month,
        this.drillData.dbtGoId, this.drillData.dbtSchemeId, this.drillData.lotteryRunId, this.divisionId, 0, true)
      .subscribe((result: LotteryDrillDownRow[]) => {
        this.filteredSubDivisions = result || [];
        this.totalTarget = this.sumBy('totalTarget');
        this.totalApplied = this.sumBy('applicationsReceived');
        this.totalAssigned = this.sumBy('benefittedCount');
        this.totalBalanceTarget = this.sumBy('balanceTarget');
        this.totalVillageCount = this.sumBy('villageCount');
        this.applyFilters();
        this.loading = false;
      }, () => {
        this.loading = false;
      });
  }

  get pageHeading(): string {
    if (!this.drillData) {
      return '';
    }
    const schemeComponent = [this.drillData.schemeName, this.drillData.productName, this.drillData.goName, this.drillData.componentName]
      .filter(v => !!v).join(' | ');
    return `${this.drillData.departmentName || ''} | ${this.drillData.financialYearName || ''} | ${schemeComponent}`;
  }

  private sumBy(key: keyof LotteryDrillDownRow): number {
    return this.filteredSubDivisions.reduce((sum, d) => sum + Number(d[key]), 0);
  }

  private applyFilters(): void {
    let list = this.filteredSubDivisions;
    if (this.showOnlyWithTarget) {
      list = list.filter(s => Number(s.totalTarget) > 0);
    }
    if (this.searchText) {
      list = list.filter(s => s.name.trim().toLowerCase().startsWith(this.searchText));
    }
    this.subDivisions = list;
  }

  onTargetFilterChange(): void {
    this.applyFilters();
  }

  viewSubDivision(subDivision: LotteryDrillDownRow): void {
    this.selectedSubDivision = { id: subDivision.id, name: subDivision.name };
    this.villageView = true;
  }

  onVillageClosed(oneLevelBack: boolean): void {
    if (oneLevelBack) {
      this.villageView = false;
    } else {
      this.onCancel();
    }
  }

  onVillageToDashboard(): void {
    this.toDashboardComp.emit(true);
  }

  viewSubsidyPattern(subDivision: LotteryDrillDownRow): void {
    this.openSubsidyPattern('Sub-Division', subDivision.name, this.divisionId, subDivision.id, 0);
  }

  onCancel(): void {
    this.blockClosed.emit(true);
  }

  viewOverallSubsidyPattern(): void {
    this.openSubsidyPattern('District', this.divisionName, this.divisionId, 0, 0);
  }

  private openSubsidyPattern(levelLabel: string, name: string, divisionId: number, subDivisionId: number, villageId: number): void {
    this.lotteryService.getSubsidyPattern(
      this.drillData.financialYearId, this.drillData.departmentId, this.drillData.month,
      this.drillData.dbtGoId, this.drillData.dbtSchemeId, this.drillData.lotteryRunId,
      divisionId, subDivisionId, villageId, true
    ).subscribe((result: LotterySubsidyPattern) => {
      this.dialog.open(SubsidyPatternViewComponent, {
        autoFocus: false,
        width: '900px',
        panelClass: 'mat-dialog-serviceschedule',
        data: {
          levelLabel, name,
          financialYear: this.drillData.financialYearName,
          goid: this.drillData.goName,
          scheme: this.drillData.schemeName,
          component: this.drillData.componentName,
          product: this.drillData.productName,
          remainingTarget: result?.remainingTarget ?? 0,
          remainingSubsidyAmount: result?.remainingSubsidyAmount ?? 0,
          rows: result?.rows ?? []
        }
      });
    });
  }

  onSearchChange(event): void {
    this.searchText = event.target.value.trim().toLowerCase();
    this.applyFilters();
  }

  goToDashboard(): void {
    this.toDashboardComp.emit(true);
  }

  exportAsXLSX(): void {
    const data = this.subDivisions.map(s => ({
      Name: s.name,
      LotteryExecutedVillages: s.villageCount,
      TotalTarget: s.totalTarget,
      ApplicationsReceived: s.applicationsReceived,
      BenefittedCount: s.benefittedCount,
      BalanceTarget: s.balanceTarget,
      ApplicationPercentage: s.applicationPercentage,
      BenefittedPercentage: s.benefittedPercentage
    }));
    this.excelService.exportAsExcelFile(data, 'Sub-Division Breakdown', this.excelColumns);
  }
}
